import { FavoriteDocumentsDataSource } from "@/models/dashboard/WidgetConfig";
import {
  FavoriteLegislation,
  FavoriteWorkspaceDocument,
} from "@/models/users/User";

export type FavoriteDocumentItem =
  | {
      id: string;
      type: "legislation";
      document: FavoriteLegislation;
    }
  | {
      id: string;
      type: "workspaceDocument";
      document: FavoriteWorkspaceDocument;
    };

export const getFavoriteLegislationId = (document: FavoriteLegislation): string =>
  `legislation:${document.internalId ?? document.externalId}`;

export const getFavoriteWorkspaceDocumentId = (
  document: FavoriteWorkspaceDocument
): string => `workspace:${document.workspaceId}:${document.documentId}`;

export const buildFavoriteDocumentItems = (
  favoriteLegislations: FavoriteLegislation[],
  favoriteWorkspaceDocuments: FavoriteWorkspaceDocument[]
): FavoriteDocumentItem[] => [
  ...favoriteLegislations.map((document) => ({
    id: getFavoriteLegislationId(document),
    type: "legislation" as const,
    document,
  })),
  ...favoriteWorkspaceDocuments.map((document) => ({
    id: getFavoriteWorkspaceDocumentId(document),
    type: "workspaceDocument" as const,
    document,
  })),
];

export const filterFavoriteDocuments = (
  items: FavoriteDocumentItem[],
  dataSource: FavoriteDocumentsDataSource = "all"
): FavoriteDocumentItem[] => {
  if (dataSource === "all") return items;
  if (dataSource === "sources") {
    return items.filter((item) => item.type === "legislation");
  }
  return items.filter((item) => item.type === "workspaceDocument");
};

const sortByOrderList = <T extends { id: string }>(
  items: T[],
  orderList: string[]
): T[] => {
  if (orderList.length === 0) return items;

  const orderMap = new Map(orderList.map((id, idx) => [id, idx]));
  return [...items].sort((a, b) => {
    const orderA = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const orderB = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
};

export interface ResolveItemOrderOptions {
  globalOrder?: string[];
  itemOrder?: string[];
  useGlobalOrder?: boolean;
}

/** Whether widget config should drive order (explicit override or legacy itemOrder-only dashboards). */
export const usesWidgetItemOrder = (
  useGlobalOrder?: boolean,
  itemOrder?: string[]
): boolean =>
  useGlobalOrder === false ||
  (useGlobalOrder === undefined && !!itemOrder?.length);

/**
 * When updating global order from a filtered widget, merge the visible
 * reorder into the full favorite list so the API receives every id once.
 */
export const buildFullDisplayOrderForGlobalUpdate = (
  visibleOrderedIds: string[],
  allItemIds: string[],
  currentGlobalOrder?: string[]
): string[] => {
  if (visibleOrderedIds.length === allItemIds.length) {
    return visibleOrderedIds;
  }

  const allItemIdSet = new Set(allItemIds);

  const baseOrder =
    currentGlobalOrder && currentGlobalOrder.length > 0
      ? (() => {
          const globalOrderSet = new Set(currentGlobalOrder);
          return [
            ...currentGlobalOrder.filter((id) => allItemIdSet.has(id)),
            ...allItemIds.filter((id) => !globalOrderSet.has(id)),
          ];
        })()
      : [...allItemIds];

  const visibleSet = new Set(visibleOrderedIds);
  const reorderedVisible = visibleOrderedIds.filter((id) =>
    allItemIdSet.has(id)
  );

  let visibleIndex = 0;
  return baseOrder.map((id) => {
    if (!visibleSet.has(id)) {
      return id;
    }
    return reorderedVisible[visibleIndex++] ?? id;
  });
};

/**
 * Resolves display order:
 * - Widget itemOrder when useGlobalOrder is false, or legacy dashboards with
 *   itemOrder but no useGlobalOrder field (backward compatible).
 * - Else global order from API when present.
 * - Else itemOrder fallback, then input order.
 */
export const resolveItemOrder = <T extends { id: string }>(
  items: T[],
  options: ResolveItemOrderOptions
): T[] => {
  const { globalOrder, itemOrder, useGlobalOrder } = options;

  if (useGlobalOrder === true && globalOrder && globalOrder.length > 0) {
    return sortByOrderList(items, globalOrder);
  }

  const hasLegacyWidgetOrder =
    useGlobalOrder === undefined && itemOrder && itemOrder.length > 0;
  const hasExplicitWidgetOrder =
    useGlobalOrder === false && itemOrder && itemOrder.length > 0;

  if ((hasLegacyWidgetOrder || hasExplicitWidgetOrder) && itemOrder) {
    return sortByOrderList(items, itemOrder);
  }

  if (globalOrder && globalOrder.length > 0) {
    return sortByOrderList(items, globalOrder);
  }

  if (itemOrder && itemOrder.length > 0) {
    return sortByOrderList(items, itemOrder);
  }

  return items;
};
